/**
 * The automation page, with the internal `skill_builder` agent as a panel
 * beside it (it asks what it needs, then writes the automation). The page is
 * the editor, as in Cursor; the builder opens from "Ask the Builder". Ways in:
 *
 *   ?new=1[&starter=<id>|&duplicate=<id>]
 *                                a new, unsaved automation: blank, from a
 *                                starter or a copy; Save creates it, and "Ask
 *                                the Builder" opens the conversation with
 *                                what's typed waiting in its composer
 *   ?from_thread=<id>            "Save as Automation" from a case conversation;
 *                                the builder opens and is asked to read it
 *   ?skill=<id>                  an existing automation; its builder
 *                                conversation reopens when the attorney asks
 *   ?skill=<id>&from_run=<run>   "Improve from this run": the builder is asked
 *                                to read that run and propose a fix
 *   ?skill=<id>&optimize=1       "Optimize With Builder" after a save: the
 *                                builder is asked to tighten the instructions
 */
import { ArrowLeft, ChevronRight, PanelLeftClose, Wand2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { IconTile } from "@/components/ui/icon-tile";
import { Spinner } from "@/components/ui/spinner";
import { firmKnowledgeApi } from "@/api/firm-knowledge";
import { AgentComposerBar } from "@/features/agents/components/AgentComposerBar";
import { AgentTranscript } from "@/features/agents/components/AgentTranscript";
import type { AgentScope, AgentSpec } from "@/features/agents/types";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { useScopedAgentChat } from "@/features/agents/useScopedAgentChat";
import { ThreadSwitcher } from "@/features/caseIde/chat/components/ThreadSwitcher";
import type {
  InvalidateTarget,
  OpenArtifactData
} from "@/features/caseIde/types";

import { invalidateActiveFirmSkills } from "@/features/firmKnowledge/hooks/useActiveFirmSkills";
import type { FirmKnowledgeScope } from "@/features/firmKnowledge/hooks/useFirmScope";
import type { FirmSkillsState } from "@/features/firmKnowledge/hooks/useFirmSkills";
import { automationsPath } from "@/features/automations/routes";
import type {
  FirmLibraryEntry,
  FirmSkill
} from "@/features/firmKnowledge/types";
import { SKILL_TEST_STATE, SkillPreview } from "./SkillPreview";
import { browserTimezone, type SkillContent } from "./SkillSettings";
import { blankSkill, copySkill, starterById } from "./skillModel";

export const SKILL_BUILDER_OWNER = "skill-builder";

/** What the builder's panel says about itself (mirrors `registry.SKILL_BUILDER`). */
export const SKILL_BUILDER_SPEC: AgentSpec = {
  kind: "skill_builder",
  scope_kind: "practice",
  label: "Automation Builder",
  description:
    "Turns how the firm does one kind of task into an automation its agents run.",
  greeting:
    "Describe a piece of work your firm does the same way every time, and I will write it up as an automation your agents can run.",
  starters: [
    "Draft an employment verification letter the way we do it",
    "Prepare an RFE response packet in our house style",
    "Run our monthly case status check"
  ],
  supports_plan_mode: false
};

/** The first message for a builder seeded from a case conversation. */
export const fromThreadPrompt = (threadId: string): string =>
  `Turn the workflow in case thread ${threadId} into a reusable automation: read its transcript, generalise what happened (no client names, dates or document contents), and ask me what you still need before writing it.`;

/** The first message for a builder opened on a new skill; null when nothing is typed. */
export const fromDraftPrompt = (draft: SkillContent): string | null => {
  const title = draft.title.trim();
  const description = draft.description_auto ? "" : draft.description.trim();
  const steps = draft.instructions.trim();
  const parts = [
    title ? `Title: ${title}` : "",
    description ? `What it's for: ${description}` : "",
    steps ? `Steps so far:\n${steps}` : ""
  ].filter(Boolean);
  return parts.length
    ? `Help me finish this automation. Ask me what you still need, then write it.\n\n${parts.join(
        "\n"
      )}`
    : null;
};

/** The message that asks the builder to learn from one run of an automation. */
export const fromRunPrompt = (runId: string, skillTitle: string): string =>
  `Look at run ${runId} of the automation "${skillTitle}": read the run's record, the attorney's feedback and what the assistant did, tell me in plain terms what went wrong or what was asked for, and propose the smallest change to the automation that would fix it. Ask me before saving.`;

/** Stands in for a run id: the run seed asks to tighten the instructions instead. */
const OPTIMIZE_SEED = "optimize";

/** The message that asks the builder to tighten an automation's instructions. */
export const optimizePrompt = (skillTitle: string): string =>
  `Tighten the instructions of the automation "${skillTitle}": keep the firm's intent and wording where it is clear, make each step specific and unambiguous, name the expected files the same way the steps do, and say what is produced at the end. Show me what you would change and ask me before saving.`;

type Props = {
  practiceScope: AgentScope;
  skills: FirmSkillsState;
  firm: FirmKnowledgeScope;
  library: FirmLibraryEntry[];
};

export const SkillBuilderView: FC<Props> = ({
  practiceScope,
  skills,
  firm,
  library
}) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const skillParam = params.get("skill");
  const fromThread = params.get("from_thread");
  const fromRun = params.get("from_run");
  const optimize = params.get("optimize") === "1";
  const isNew = params.get("new") === "1";
  const starterId = params.get("starter");
  const duplicateId = starterId ? null : params.get("duplicate");
  // The page is the editor; the conversation is a click away, open from the
  // start only when the builder was asked something (a thread, a run).
  const [chatOpen, setChatOpen] = useState(
    Boolean(fromThread) || Boolean(fromRun) || optimize
  );
  const lawFirmId = firm.lawFirmId ?? "";
  const original = duplicateId
    ? skills.skills.find((s) => s.id === duplicateId) ?? null
    : null;
  const awaitingOriginal = Boolean(duplicateId) && !original && skills.loading;
  const blank = useMemo(
    () =>
      original
        ? copySkill(original)
        : blankSkill(starterById(starterId), {
            lawFirmId,
            timezone: browserTimezone()
          }),
    // The copy is taken once, from the list row as it was when opened.
    [starterId, lawFirmId, original?.id]
  );
  const draftKey = starterId
    ? `starter:${starterId}`
    : original
    ? `duplicate:${original.id}`
    : "blank";

  // The skill shown on the right: the URL's, else the one the builder just
  // wrote (its `data-open-artifact`). Leaving `skill=` (Duplicate goes to
  // `new=1` on the same mounted view) clears it.
  const [openedId, setOpenedId] = useState<string | null>(skillParam);
  useEffect(() => {
    setOpenedId(skillParam);
  }, [skillParam]);
  const listed = openedId ? skills.skills.find((s) => s.id === openedId) : null;
  // The list DTO has no rendered markdown; fetch the full row for the preview.
  const [full, setFull] = useState<FirmSkill | null>(null);
  useEffect(() => {
    if (!openedId) {
      setFull(null);
      return undefined;
    }
    let cancelled = false;
    firmKnowledgeApi
      .getSkill(openedId, firm.scope)
      .then((s) => {
        if (!cancelled) setFull(s);
      })
      .catch(() => {
        if (!cancelled) setFull(null);
      });
    return () => {
      cancelled = true;
    };
    // Refetch when the list's copy changes (a save, an enable, a builder write).
  }, [
    openedId,
    firm.scope,
    listed?.version,
    listed?.status,
    listed?.updated_at
  ]);
  const shown: FirmSkill | null =
    full && listed
      ? {
          ...listed,
          markdown: full.markdown,
          created_by_name: full.created_by_name,
          updated_by_name: full.updated_by_name
        }
      : listed ?? full;

  const onInvalidate = useCallback(
    (targets: InvalidateTarget[]) => {
      if (targets.includes("firm_skills")) {
        void skills.refresh();
        invalidateActiveFirmSkills();
      }
    },
    [skills]
  );
  const onOpenArtifact = useCallback(
    (artifact: OpenArtifactData) => {
      // The builder's `create_firm_skill` / `update_firm_skill` open the skill
      // beside the conversation. (Not one of the Case IDE's artifact kinds.)
      if ((artifact.kind as string) === "firm_skill") {
        setOpenedId(artifact.id);
        void skills.refresh();
        navigate(automationsPath({ skillId: artifact.id }), {
          replace: true
        });
      }
    },
    [navigate, skills]
  );

  const scoped = useScopedAgentChat({
    agent: "practice",
    agentKind: "skill_builder",
    scope: practiceScope,
    spec: SKILL_BUILDER_SPEC,
    owner: SKILL_BUILDER_OWNER,
    initialThreadId: listed?.builder_thread_id ?? null,
    adapters: { onInvalidate, onOpenArtifact }
  });
  const { chat, empty, starters, send } = scoped;

  // "Ask the Builder" on a new skill puts what's typed in the composer for
  // the attorney to finish and send; the composer registers how to.
  const promptRef = useRef<((text: string) => void) | null>(null);
  const pendingPromptRef = useRef<string | null>(null);
  const onPromptRequest = useCallback((register: (text: string) => void) => {
    promptRef.current = register;
    if (pendingPromptRef.current) {
      register(pendingPromptRef.current);
      pendingPromptRef.current = null;
    }
    return () => {
      if (promptRef.current === register) promptRef.current = null;
    };
  }, []);
  const putInComposer = useCallback((text: string) => {
    if (promptRef.current) promptRef.current(text);
    else pendingPromptRef.current = text;
  }, []);

  // "Save as skill" and "Improve from this run" start on a fresh thread and
  // send their first message once that thread is the active, restored one;
  // "Ask the Builder" on a new skill starts one too but only fills the composer.
  const seedRef = useRef<{
    threadId: string | null;
    text: string;
    send: boolean;
  } | null>(
    fromThread
      ? { threadId: null, text: fromThreadPrompt(fromThread), send: true }
      : null
  );
  const startedRef = useRef(false);
  const [seedAsked, setSeedAsked] = useState(0);
  useEffect(() => {
    const seed = seedRef.current;
    if (!seed || startedRef.current || chat.threadsLoading) return;
    startedRef.current = true;
    void chat.newThread().then((t) => {
      seedRef.current = { ...seed, threadId: t.id };
    });
  }, [chat, chat.threadsLoading, seedAsked]);
  useEffect(() => {
    const seed = seedRef.current;
    if (!seed?.threadId || chat.threadId !== seed.threadId) return;
    if (chat.restoring || chat.status !== "ready") return;
    seedRef.current = null;
    if (seed.send) void chat.send(seed.text);
    else putInComposer(seed.text);
    // Drop the one-shot seed (`from_thread`, `from_run`) so a reload does not
    // send it again, but keep the skill or `new=1` (and its starter or
    // original, which the unsaved page is filled from) or the tab leaves the page.
    navigate(
      automationsPath(
        skillParam
          ? { skillId: skillParam }
          : { newSkill: true, starter: starterId, duplicate: duplicateId }
      ),
      { replace: true, state: null }
    );
  }, [
    chat,
    chat.threadId,
    chat.restoring,
    chat.status,
    navigate,
    putInComposer,
    skillParam,
    starterId,
    duplicateId
  ]);

  // "Improve from this run" continues the conversation that wrote the skill
  // when there is one (and it is loaded); otherwise it starts a new thread
  // through the seed path above.
  const runSeedRef = useRef<string | null>(
    skillParam ? fromRun ?? (optimize ? OPTIMIZE_SEED : null) : null
  );
  // The page stays mounted when a save sends `?optimize=1` to it, so the
  // ask is taken from the URL as well as on mount.
  useEffect(() => {
    if (!skillParam || !(fromRun || optimize)) return;
    runSeedRef.current = fromRun ?? OPTIMIZE_SEED;
    setChatOpen(true);
  }, [skillParam, fromRun, optimize]);
  useEffect(() => {
    const runId = runSeedRef.current;
    if (!runId || !listed || chat.threadsLoading) return;
    const name = listed.title || listed.slug;
    const text =
      runId === OPTIMIZE_SEED
        ? optimizePrompt(name)
        : fromRunPrompt(runId, name);
    const target = listed.builder_thread_id;
    if (target && chat.threads.some((t) => t.id === target)) {
      if (chat.threadId !== target) {
        chat.setThreadId(target);
        return;
      }
      if (chat.restoring || chat.status !== "ready") return;
      runSeedRef.current = null;
      void chat.send(text);
      navigate(automationsPath({ skillId: listed.id }), {
        replace: true,
        state: null
      });
      return;
    }
    runSeedRef.current = null;
    if (!startedRef.current)
      seedRef.current = { threadId: null, text, send: true };
  }, [
    chat,
    chat.threadId,
    chat.threads,
    chat.threadsLoading,
    chat.restoring,
    chat.status,
    listed,
    navigate,
    fromRun,
    optimize
  ]);

  // Opening an existing skill reopens the conversation that wrote it (once
  // per skill; the chat is read through a ref so a later thread switch by
  // the user is not undone).
  const builderThread = listed?.builder_thread_id ?? null;
  const chatRef = useRef(chat);
  chatRef.current = chat;
  useEffect(() => {
    const { current } = chatRef;
    if (!builderThread || current.threadsLoading) return;
    if (
      current.threadId !== builderThread &&
      current.threads.some((t) => t.id === builderThread)
    )
      current.setThreadId(builderThread);
  }, [builderThread, chat.threadsLoading]);

  const title = useMemo(
    () =>
      shown
        ? shown.title || shown.slug
        : isNew || fromThread
        ? "New Automation"
        : "Automations",
    [shown, isNew, fromThread]
  );

  const breadcrumb = (
    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 items-center gap-1 text-base"
    >
      <button
        type="button"
        className="shrink-0 text-fg-3 transition-colors hover:text-fg"
        onClick={() => navigate(automationsPath())}
      >
        Automations
      </button>
      <ChevronRight className="size-3.5 shrink-0 text-fg-4" />
      <span className="truncate text-fg">{title}</span>
    </nav>
  );

  const askBuilder = (draft: SkillContent) => {
    const text = shown ? null : fromDraftPrompt(draft);
    if (text && !startedRef.current) {
      seedRef.current = { threadId: null, text, send: false };
      setSeedAsked((n) => n + 1);
    } else if (text) {
      putInComposer(text);
    }
    setChatOpen(true);
  };

  const backToSkills = () => navigate(automationsPath());

  const page = (leading: ReactNode) =>
    shown ? (
      <SkillPreview
        key={shown.id}
        skill={shown}
        skills={skills}
        firm={firm}
        library={library}
        leading={leading}
        onAskBuilder={chatOpen ? undefined : askBuilder}
      />
    ) : openedId || awaitingOriginal || !firm.permissionsReady ? (
      <div className="flex h-full items-center justify-center gap-2 text-base text-fg-3">
        <Spinner className="size-3.5" /> Loading the automation…
      </div>
    ) : (
      <SkillPreview
        key={`new-${draftKey}`}
        skill={blank}
        skills={skills}
        firm={firm}
        library={library}
        leading={leading}
        isNew
        draftKey={draftKey}
        onAskBuilder={chatOpen ? undefined : askBuilder}
        onCreated={(saved, opts) => {
          setOpenedId(saved.id);
          navigate(
            automationsPath({ skillId: saved.id, optimize: opts?.optimize }),
            {
              replace: true,
              state: opts?.test ? { [SKILL_TEST_STATE]: true } : undefined
            }
          );
        }}
        onDiscard={backToSkills}
      />
    );

  // One tree whether or not the conversation is open, so opening it keeps
  // the page (and an unsaved skill's draft) mounted.
  return (
    <div className="flex h-full min-h-0" data-testid="skill-builder">
      {chatOpen ? (
        <section className="flex w-105 min-w-90 shrink-0 flex-col border-r border-line-2">
          <header className="flex h-10 shrink-0 items-center gap-1.5 border-b border-line-2 px-3">
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Back to automations"
              onClick={backToSkills}
              data-testid="skill-builder-back"
            >
              <ArrowLeft />
            </Button>
            <AgentMoodAvatar
              animated
              varied
              size="sm"
              agent={{ agent_kind: "skill_builder" }}
            />
            <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
              <span className="shrink-0 text-base font-medium text-fg">
                Automation Builder
              </span>
              <span className="truncate text-sm text-fg-3">{title}</span>
            </div>
            <ThreadSwitcher
              compact
              threads={chat.threads.filter((t) => !t.archived)}
              activeId={chat.threadId}
              onSelect={chat.setThreadId}
              onNew={() => void chat.newThread()}
              onDelete={(id) => void chat.deleteThread(id)}
              disabled={chat.threadsLoading}
            />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Hide the builder"
              onClick={() => setChatOpen(false)}
              data-testid="skill-builder-collapse"
            >
              <PanelLeftClose />
            </Button>
          </header>
          {empty ? (
            <div
              className="flex min-h-0 flex-1 flex-col justify-end gap-1.5 px-4 pb-4"
              data-testid="skill-builder-greeting"
            >
              <IconTile icon={Wand2} size="lg" className="mb-2 text-purple" />
              <h2 className="text-lg font-medium text-fg">
                Let's Write an Automation
              </h2>
              <p className="text-base text-fg-3">
                {SKILL_BUILDER_SPEC.greeting}
              </p>
            </div>
          ) : (
            <AgentTranscript scoped={scoped} />
          )}
          <AgentComposerBar
            scoped={scoped}
            className="px-3 pb-2"
            placeholder="Describe the workflow, or answer the builder's questions"
            hideDisclaimer
            onPromptRequest={onPromptRequest}
            above={
              empty && starters.length ? (
                <div className="flex flex-col items-start gap-2 pb-1">
                  {starters.map((s) => (
                    <Button
                      key={s}
                      variant="pill"
                      size="xs"
                      className="h-auto max-w-full py-1 text-left whitespace-normal"
                      onClick={() => send(s)}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              ) : null
            }
          />
        </section>
      ) : null}

      <section className="min-w-0 flex-1">
        {page(
          chatOpen ? (
            <span className="pl-1">{breadcrumb}</span>
          ) : (
            <div
              className="flex min-w-0 items-center gap-1"
              data-testid="skill-builder-rail"
            >
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Back to automations"
                onClick={backToSkills}
                data-testid="skill-builder-back"
              >
                <ArrowLeft />
              </Button>
              {breadcrumb}
            </div>
          )
        )}
      </section>
    </div>
  );
};
